import {
  Building2,
  Clock,
  Ellipsis,
  Landmark,
  Mail,
  Plug,
  Printer,
  Rocket,
  ReceiptText,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Tags,
  Truck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { hasLabelRight } from '@till-erp/documents/values';
import type { Me, SettingGroupSummary } from './lib/api.ts';
import { searchText } from './lib/setting-card.ts';

/**
 * Einstellungen: erweiterbare Hauptpunkte mit Reitern (Change „Einstellungen ordnen“, Rudolf 07.10.2026; Story 1.41).
 * Adressen `/einstellungen/<hauptpunkt>/<reiter>`. Eigene Seiten tragen hier ihren Pfad ein und haben eine feste Route;
 * Reiter nur aus Gruppen zeigt die Route `/einstellungen/$bereich/$reiter` mit den Einstellungs-Karten. Alte Adressen
 * (vor dem 07.10.2026) leiten über `legacySettingsTarget` um.
 */
export const SETTINGS_PATH = '/einstellungen';
/**
 * Startseite „Auf einen Blick“ (Ticket 1.209, Muster `seiten-s3/einstellungen.html`, U22): erster Eintrag der Navigation
 * vor „Firma“, nur lesen (Zugänge, Betriebsschalter, fehlende Pflichtangaben, zuletzt geändert).
 */
export const SETTINGS_OVERVIEW_PATH = '/einstellungen/ueberblick';

/**
 * Höchstzahl sichtbarer Reiter im Seitenkopf (Baustein C, DESIGN „Reiter der Einstellungen“): mehr als 7 Reiter → die
 * ersten 6 sichtbar, die übrigen unter „Weitere ▾“ (erst ab 1024 px; am Handy wischt das Segment). Reine UI-Schwelle.
 */
export const SETTINGS_TABS_MAX = 7;

/** Reiter aufteilen: bis `max` alle sichtbar, darüber die ersten `max - 1` sichtbar und der Rest im Menü „Weitere“. */
export function splitSettingsTabs<T>(tabs: readonly T[], max = SETTINGS_TABS_MAX): { visible: T[]; hidden: T[] } {
  if (tabs.length <= max) return { visible: [...tabs], hidden: [] };
  const keep = Math.max(1, max - 1);
  return { visible: tabs.slice(0, keep), hidden: tabs.slice(keep) };
}

/**
 * Tabellen-Einstellungen, die als kompakte Werte-Liste (40-px-Zeilen, Bearbeiten in der Zelle; Baustein C) statt als
 * Tabelle mit offenen Feldern erscheinen. Nur Darstellung. Ticket 1.262: Verkauf › Aufträge, Lager & Versand › Lager,
 * Shop › Shopware; weitere Fachseiten tragen ihre Schlüssel mit 1.263 ein.
 */
export const VALUE_LIST_SETTING_KEYS: ReadonlySet<string> = new Set([
  'example_shipping_rates',
  // Verkauf › Aufträge
  'shipping_method_carrier_products',
  'payment_method_categories',
  'pdf_draft_shipping_methods',
  'order_invoice_countries',
  // Lager & Versand › Lager
  'inventory_correction_reasons',
  'stock_obsolescence_discounts',
  'article_tax_classes',
  'article_units',
  'article_delivery_times',
  'article_price_formula_rounds',
  // Shop › Shopware
  'shopware_piece_unit_ids',
  'shopware_status_table',
  'shopware_mail_cutover',
  'shopware_tax_ids_by_class',
  // KI (Ticket 1.263)
  'ai_models',
  'ai_agent_switch',
  'ai_model_classes',
  'ai_budget',
  'ai_agent_work_area',
  // Belege (Ticket 1.263)
  'document_number_ranges',
  'document_retention_years',
  'document_layout_footer',
  'invoice_change_table',
]);

/** Tabellen-Einstellungen mit einer Text-Spalte, die als Chips („+“, × am Chip) erscheinen (Baustein C). */
export const CHIP_SETTING_KEYS: ReadonlySet<string> = new Set(['example_tags', 'custom_field_html_tags']);

/**
 * Ticket 1.262: Text-Spalten von Werte-Listen, die Vorschläge aus `GET /api/v1/settings/choices` als Auswahl anbieten
 * (Einstellung → Spalte → Quelle). Versand-/Zahlarten aus gelesenen Shopware-Aufträgen, Einheiten-IDs aus gelesenen
 * Artikeln. Eigener Name bleibt über „+ „Text“ neu anlegen“ möglich; ohne Vorschläge Freitext.
 */
export const SETTING_CHOICE_COLUMNS: Readonly<Record<string, Readonly<Record<string, 'shippingMethods' | 'paymentMethods' | 'shopwareUnits'>>>> = {
  shipping_method_carrier_products: { shipping_method: 'shippingMethods' },
  pdf_draft_shipping_methods: { shipping_method: 'shippingMethods' },
  payment_method_categories: { payment_method: 'paymentMethods' },
  shopware_piece_unit_ids: { id: 'shopwareUnits' },
  article_units: { shopwareId: 'shopwareUnits' },
};

/** Lange Texte, die eingeklappt erscheinen (3 Zeilen sichtbar, „Ganz zeigen“/„Weniger zeigen“; Ticket 1.262). */
export const COLLAPSED_TEXT_SETTING_KEYS: ReadonlySet<string> = new Set(['inventory_count_instruction']);

/**
 * Feldbreite abweichend von der Regel nach Höchstlänge (Ticket 1.262): Adresse und Kennung sind lange erlaubt, im
 * Alltag aber kurz – `l` bzw. `m` statt voller Breite.
 */
export const SETTING_WIDTH_OVERRIDES: Readonly<Record<string, 'xs' | 's' | 'm' | 'l'>> = {
  shopware_api_url: 'l',
  shopware_client_id: 'm',
  // Story 26.16 (Sichtung HS55): bis 5 Ziffern („10080“) plus „Minuten“ im Feld – schmal schnitt die Zahl ab.
  counterpart_heartbeat_max_minutes: 'm',
};

/** Ticket 1.262: Kürzel-Feld, das als Auswahl aus der Tabelle der Steuerklassen (`article_tax_classes`) erscheint. */
export const TAX_CLASS_CHOICE = { setting: 'article_default_tax_class', table: 'article_tax_classes' } as const;
/**
 * Selten nötige, technische Einstellungen: stehen in ihrer Karte eingeklappt unter „Erweitert – Selten nötig“
 * (Ticket 1.209). Nur Darstellung – Werte, Rechte und Prüfregeln bleiben unverändert in `settings`. Eine Stelle für alle
 * Karten; weitere Schlüssel erst nach Rudolfs Freigabe der Liste.
 */
export const ADVANCED_SETTING_KEYS: ReadonlySet<string> = new Set([
  'article_max_variants_per_create',
  'article_max_option_groups',
  'article_max_price_tiers',
  'shop_data_max_kb',
  'shop_tags_max',
  'shop_text_max_length',
  'credit_limit_default_currency',
  'customer_note_max_length',
  'mail_attachment_max_mb',
  'mail_signature_max_chars',
  'unsubscribe_rate_per_minute',
  'document_render_retry_base_seconds',
  'document_render_retry_max_seconds',
  'document_render_max_attempts',
  'ai_content_purge_time',
  'ai_content_purge_batch_size',
  'ai_content_purge_max_batches',
  'ops_error_report_retention_days',
  'shopware_image_max_mb',
  'shopware_image_max_megapixels',
  'shopware_image_retry_hours',
  'shopware_image_max_attempts',
  'shopware_field_catalog_hour',
  'shopware_field_catalog_sample_size',
  'shopware_sync_batch_size',
  'shopware_sync_batch_wait_seconds',
  'metrics_block_size',
  'metrics_stale_grace_minutes',
  // Ticket 1.226 (D11): Shopware, Belege allgemein, Verkauf › Aufträge, Technik › Listen und Zeitjobs.
  'shopware_stock_bundle_seconds',
  'shopware_field_default_lead',
  'shopware_field_default_variant_rule',
  'shopware_write_language_id',
  'shopware_image_sync_minutes',
  'shopware_image_full_read_hours',
  'shopware_order_read_overlap_minutes',
  'shopware_order_full_check_days',
  'shopware_order_full_check_hour',
  'shopware_customer_sync_minutes',
  'shopware_customer_full_read_hours',
  'shopware_category_sync_minutes',
  'shopware_import_max_hits',
  'shopware_import_price_change_warn_percent',
  'shopware_import_preview_expiry_days',
  'document_number_counter_digits',
  'sales_period_short_days',
  'sales_period_long_days',
  'sales_window_refresh_time',
  'order_check_follow_up_days',
  'order_check_explanation_max_length',
  'order_tags_max',
  'mass_edit_max_fields',
  'mass_edit_block_size',
  'mass_edit_price_change_warn_percent',
  'mass_edit_reminder_hours',
  'mass_edit_sentence_max_length',
  'mass_edit_sentence_max_options',
  'mass_edit_sentence_max_assumptions',
  'mass_edit_sentence_assumption_length',
  'list_metrics_timeout_ms',
  'list_filter_max_relative_days',
  'list_search_max_terms',
  'search_max_length',
  'search_min_term_length',
  'search_hits_per_group',
  'list_export_max_cells',
  'list_group_max',
  'list_count_threshold',
  'list_watch_max_rows',
  'data_question_max_length',
  'data_question_history',
  'data_question_table_rows',
  'data_question_max_options',
  'data_question_max_assumptions',
  'data_question_assumption_length',
  'record_chart_max_days',
  'metric_period_short_days',
  'metric_period_medium_days',
  'metric_period_long_days',
  'timed_job_ai_purge_expired_content_catch_up',
  'timed_job_ecb_fetch_rates_catch_up',
  'timed_job_reporting_watch_lists_catch_up',
]);
export const USERS_PATH = '/einstellungen/firma/benutzer';
export const COUNTERPARTS_PATH = '/einstellungen/schnittstellen/uebersicht';
/** Beispielrechnungen einer Gegenstelle (Story 1.8): Datensatz-Adresse, bleibt wie vor dem 07.10.2026. */
export const EXAMPLE_INVOICES_PATH = '/einstellungen/schnittstellen/beispiel-rechnungen';
export const SWITCHES_PATH = '/einstellungen/schnittstellen/betriebsschalter';
export const CREDENTIALS_PATH = '/einstellungen/schnittstellen/zugaenge';
/** Einstellungen › Schnittstellen › Wechselkurse (Story 5.86): EZB-Kurse, Verlauf, Abruf-Protokoll; „Jetzt abrufen“ nur Admins. */
export const EXCHANGE_RATES_PATH = '/einstellungen/schnittstellen/wechselkurse';
/** Einstellungen › Start & Umschalttag › Probephase (Story 8.1): alle sehen den Zustand, umstellen nur Admins. */
export const TRIAL_PATH = '/einstellungen/start/probephase';
/** Einstellungen › Start & Umschalttag › Vergleichsbericht (Story 8.7): alle sehen den Bericht, Regeln nur Admins. */
export const COMPARISON_REPORT_PATH = '/einstellungen/start/vergleichsbericht';
/** Einstellungen › Start & Umschalttag › Umschalttag (Story 20.1): alle sehen Checkliste und Ablauf; Probelauf nur Admins. */
export const CUTOVER_PATH = '/einstellungen/start/umschalttag';
/** Technik › System: dort steht der Alarm-Empfänger (Einstellung `alarm_recipient`). */
export const SYSTEM_SETTINGS_PATH = '/einstellungen/technik/system';
export const TEST_CLOCK_PATH = '/einstellungen/technik/testuhr';
export const AI_PATH = '/einstellungen/ki/uebersicht';
/** Einstellungen › KI › Agenten (Story 2.19): nur Admins. */
export const AI_AGENTS_PATH = '/einstellungen/ki/agenten';
/** Einstellungen › KI › Budget (Story 2.21): nur Admins; Ziel des Glocken-Hinweises „KI-Budget zu 80 %“. */
export const AI_BUDGET_PATH = '/einstellungen/ki/budget';
/** Einstellungen › KI › Artikel-Assistent (Story 5.102): Prompts und Haken je Feld, „Testen an Beispielartikel“; nur Admins. */
export const AI_ARTICLE_ASSISTANT_PATH = '/einstellungen/ki/artikel-assistent';
/** Einstellungen › KI › MCP-Schlüssel (epic-ki-assistent-mcp Story 1): nur Admins. */
export const MCP_KEYS_PATH = '/einstellungen/ki/mcp-schluessel';
export const TAX_PATH = '/einstellungen/finanzen/steuer';
/** Einstellungen › E-Mail & Aufgaben › Vorlagen (Story 2.7): alle sehen sie, ändern nur Admins. */
export const MAIL_TEMPLATES_PATH = '/einstellungen/e-mail/vorlagen';
/** Einstellungen › Aufgaben › Labels (Story 23.4): alle sehen die Labels, ändern nur Admins. */
export const TASK_LABELS_PATH = '/einstellungen/aufgaben/labels';
/** Einstellungen › Aufgaben › Vorlagen (Story 23.12): nur Admins pflegen; „Aus Vorlage …“ im Blatt „Neue Aufgabe“ für alle. */
export const TASK_TEMPLATES_PATH = '/einstellungen/aufgaben/vorlagen';
/** Einstellungen › Aufgaben › Erinnerung (Story 23.13): Vorlauf der Erinnerung in der Glocke, nur Admins. */
export const TASK_REMINDER_PATH = '/einstellungen/aufgaben/erinnerung';
/** Einstellungen › Aufgaben › Wiederholungen (Story 23.11): alle Wiederholungen und ihr Vorlauf, nur Admins. */
export const TASK_RECURRENCE_PATH = '/einstellungen/aufgaben/wiederholungen';
export const OPERATIONS_PATH = '/einstellungen/technik/betrieb';
/**
 * Einstellungen › Technik › Archiv und Sicherung (Ticket 1.268): Zustand von Archiv, Zweitkopie, nächtlicher Sicherung
 * und Schutzprobe, darunter die Karte der Gruppe `archive_backup`. Die Wiederherstellung bleibt bei Betrieb.
 */
export const ARCHIVE_BACKUP_PATH = '/einstellungen/technik/archiv-sicherung';
export const ARCHIVE_BACKUP_GROUP = 'archive_backup';
/** Ticket 1.268: Einstellungen, die bis dahin unter Betrieb standen; alte Links `…/betrieb?feld=<schlüssel>` leiten um. */
export const ARCHIVE_BACKUP_LEGACY_FIELDS: ReadonlySet<string> = new Set(['ops_backup_max_age_hours', 'ops_archive_copy_max_minutes']);
export const SHIPPING_COSTS_PATH = '/einstellungen/lager-versand/versandkosten';
/**
 * Einstellungen › Lager & Versand › EAN-Nummern (Story 25.1): Hinweis auf doppelte EANs im Bestand mit Links; Story 25.8
 * ergänzt hier die Einstellungen des Nummern-Pools. Büro-Rollen lesen (der Packplatz nicht).
 */
export const GTIN_SETTINGS_PATH = '/einstellungen/lager-versand/ean-nummern';
/** Ticket 24: Gruppe „Versandkostenfreie Artikel“ (taxrules) als Karten unter der Versandkosten-Tabelle. */
export const SHIPPING_FREE_GROUP = 'shipping_free';
/** Einstellungen › Shop & Marktplätze › Shopware-Felder (Story 5.18): alle sehen den Feldkatalog, lesen lassen nur Admins. */
export const SHOPWARE_FIELDS_PATH = '/einstellungen/shop/shopware-felder';
/** Einstellungen › Shop & Marktplätze › Feld-Abdeckung (Story 5.29): alle sehen den Bericht, entscheiden nur Admins. */
export const SHOPWARE_FIELD_COVERAGE_PATH = '/einstellungen/shop/shopware-felder/abdeckung';
/** Einstellungen › Belege › Vorlagen (Story 3.1): alle sehen die Muster, erzeugen nur Admins. */
export const DOCUMENT_TEMPLATES_PATH = '/einstellungen/belege/vorlagen';
/** Einstellungen › Belege › Nummernkreise (Story 3.7): alle sehen Kreise und Prüfbericht, Zählerstand setzen nur Admins. */
export const NUMBER_RANGES_PATH = '/einstellungen/belege/nummernkreise';
/** Einstellungen › Datenschutz › Personendaten (Story 18.2): Verzeichnis der Personendaten, alle sehen es lesend. */
export const PRIVACY_PATH = '/einstellungen/datenschutz/personendaten';
/** Einstellungen › Datenschutz › Anonymisierungen (Story 18.4): Vorgänge „Anonymisieren auf Anfrage“, nur Admins. */
export const ANONYMIZATIONS_PATH = '/einstellungen/datenschutz/anonymisierungen';
/** Gruppe „Firma“ (Briefkopf der Belege), Reiter Firma › Firmendaten. */
export const COMPANY_GROUP = 'company';
/** Reiter aus Gruppen haben keine eigene Route (sondern `/einstellungen/$bereich/$reiter`), daher als `string`. */
export const COMPANY_PATH: string = `${SETTINGS_PATH}/firma/firmendaten`;
/** Gruppe „Belege“, Reiter Belege › Allgemein. */
export const DOCUMENTS_PATH: string = `${SETTINGS_PATH}/belege/allgemein`;
/** Story 22.1: Einstellungen › Zeiterfassung › Mitarbeiter (Liste und „Mitarbeiter anlegen“, nur Admin). */
export const STAFF_EMPLOYEES_PATH = '/einstellungen/zeiterfassung/mitarbeiter';
/** Story 22.1: Gruppe „Funktionen & Fristen“ der Zeiterfassung (Pause-Knopf, Rückgängig-Frist). */
export const STAFF_FUNCTIONS_GROUP = 'staff_functions';
/** Story 22.4: Gruppe „Arbeitszeit & Rundung“ (Modell-Vorlagen, Grenzen nach Arbeitszeitgesetz). */
export const STAFF_WORK_TIME_GROUP = 'staff_work_time';
/** Story 22.6: Gruppe „Urlaub & Abwesenheit“ (Abwesenheitsarten, Anspruch, Halbtage, Verfall, Rundung Teilanspruch). */
export const STAFF_LEAVE_GROUP = 'staff_leave';
/** Story 22.17: Gruppe „Rundung der Stempelzeiten“ (eigene Karte „Rundung je Mitarbeiter“ im Reiter Arbeitszeit & Rundung). */
export const STAFF_TIME_ROUNDING_GROUP = 'staff_time_rounding';
/**
 * Einstellungen › E-Mail & Aufgaben › Postausgang (Story 2.2): eigene Seite für die Gruppe „E-Mail“ (`mail`) mit Zustand,
 * Versandliste und den Einstellungen der Gruppe.
 */
export const MAIL_GROUP = 'mail';
export const MAIL_PATH = '/einstellungen/e-mail/postausgang';
/**
 * Einstellungen › Drucken › Etiketten (Story 27.5): Galerie der Etikett-Vorlagen; der Editor einer Vorlage liegt unter
 * `LABELS_PATH/<id>`. Sichtbar für alle, die Etiketten gestalten (`hasLabelRight(…, 'design')`: Benutzer, Packplatz, Admin).
 */
export const LABELS_PATH = '/einstellungen/drucken/etiketten';
/**
 * Einstellungen › Drucken › Drucker (Story 27.6): Druckerprofile der Etiketten mit QZ-Druckerwahl und Testdruck (ändern
 * nur Admins) und darunter die übrigen Einstellungen der Gruppe `labels` (Frist, Etiketten je Druck, Grenzen).
 */
export const LABEL_PRINTERS_PATH: string = `${SETTINGS_PATH}/drucken/drucker`;
/** Einstellungen › Drucken › Druckverlauf (Story 27.6): Liste `label_prints` über den Listen-Baukasten. */
export const LABEL_PRINTS_PATH: string = `${SETTINGS_PATH}/drucken/verlauf`;
export const LABELS_GROUP = 'labels';

/** Adresse des Editors einer Etikett-Vorlage. */
export function labelEditorPath(id: string): string {
  return `${LABELS_PATH}/${encodeURIComponent(id)}`;
}

export interface SettingsPageItem {
  label: string;
  path: string;
  /** Nur zeigen, wenn erfüllt (z. B. Testuhr nur auf der Testumgebung). */
  visible?: (me: Me) => boolean;
  /** Stichworte für die Einstellungs-Suche (Rudolf 06.10.2026), z. B. „Zähler“ für Nummernkreise. */
  keywords?: readonly string[];
}

const adminOnly = (me: Me) => me.role === 'admin';

/** Eigene Seiten (mit fester Route); Ort in der Navigation: `SETTINGS_SECTIONS`. */
export const settingsPages: readonly SettingsPageItem[] = [
  { label: 'Benutzer', path: USERS_PATH, keywords: ['Rollen', 'Admin', 'Person', 'Mitarbeiter'] },
  { label: 'Verbindungen', path: COUNTERPARTS_PATH, keywords: ['Schnittstellen', 'Zugänge', 'Zugangsdaten', 'Passwort', 'API-Schlüssel', 'Betriebsschalter', 'Schalter', 'off', 'shadow', 'live', 'Probe', 'Gegenstellen', 'Shopware', 'eBay', 'DHL'] },
  { label: 'Übersicht', path: AI_PATH, keywords: ['KI', 'OpenAI', 'Anthropic', 'Protokoll'] },
  // Agenten und Budget nur für Admins (Story 2.19, 2.21).
  { label: 'Agenten', path: AI_AGENTS_PATH, visible: adminOnly, keywords: ['KI-Agenten', 'Stufe'] },
  { label: 'Budget', path: AI_BUDGET_PATH, visible: adminOnly, keywords: ['KI-Budget', 'Kosten', 'Kostenbremse'] },
  // Artikel-Assistent nur für Admins (Story 5.102).
  { label: 'Artikel-Assistent', path: AI_ARTICLE_ASSISTANT_PATH, visible: adminOnly, keywords: ['Prompt', 'Grund-Prompt', 'Produkttexte', 'Stil', 'Alles mit KI ausfüllen'] },
  // MCP-Schlüssel nur für Admins (epic-ki-assistent-mcp Story 1, AD-20).
  { label: 'MCP-Schlüssel', path: MCP_KEYS_PATH, visible: adminOnly, keywords: ['MCP', 'Claude Code', 'KI-Assistent', 'API-Schlüssel', 'Zugriffe'] },
  { label: 'Wechselkurse', path: EXCHANGE_RATES_PATH, keywords: ['EZB', 'Kurs', 'Währung', 'USD', 'Fremdwährung'] },
  // Probephase (Story 8.1): alle sehen den Zustand; umstellen nur Admins.
  { label: 'Probephase', path: TRIAL_PATH, keywords: ['Probe', 'Probephase', 'Baustein', 'P-Nummer', 'Probe-Auftrag'] },
  // Vergleichsbericht (Story 8.7): alle sehen den Bericht; Regeln „erwartet“ nur Admins.
  { label: 'Vergleichsbericht', path: COMPARISON_REPORT_PATH, keywords: ['Vergleich', 'Xentral', 'Differenz', 'erwartet', 'unerklärt'] },
  // Umschalttag (Story 20.1): alle sehen Checkliste und Ablauf; Probelauf starten nur Admins.
  { label: 'Umschalttag', path: CUTOVER_PATH, keywords: ['Umschalttag', 'Probelauf', 'Checkliste', 'Go-live', 'Xentral', 'Probedaten'] },
  // Alle sehen Lebenszeichen und Alarme; Probealarm nur Admins.
  { label: 'Betrieb', path: OPERATIONS_PATH, keywords: ['Alarm', 'Lebenszeichen', 'Worker', 'Betrieb und Alarme'] },
  // Ticket 1.268: alle sehen den Zustand; „Archiv prüfen“ nur Admins.
  { label: 'Archiv und Sicherung', path: ARCHIVE_BACKUP_PATH, keywords: ['Sicherung', 'Backup', 'Archiv', 'Zweitkopie', 'Schutzprobe', 'restic'] },
  // Alle sehen die Steuer lesend; ändern nur Admins (D7).
  { label: 'Steuer', path: TAX_PATH, keywords: ['Umsatzsteuer', 'MwSt', 'Steuersatz'] },
  { label: 'Vorlagen', path: MAIL_TEMPLATES_PATH, keywords: ['E-Mail-Vorlagen', 'Textbausteine', 'Platzhalter'] },
  // Story 23.4: alle sehen die Labels; umbenennen, Farbe und Löschen nur Admins.
  { label: 'Labels', path: TASK_LABELS_PATH, keywords: ['Aufgaben-Labels', 'Schlagwörter', 'Farbe', 'Filter'] },
  // Story 23.12: Vorlagen für Aufgaben nur für Admins (Nutzen über „Neue Aufgabe“ › „Aus Vorlage …“ für alle).
  { label: 'Vorlagen', path: TASK_TEMPLATES_PATH, visible: adminOnly, keywords: ['Aufgaben-Vorlagen', 'Checkliste', 'Ablauf', 'Neuer Lieferant', 'Arbeitstage'] },
  // Story 23.13: Erinnerung vor Fälligkeit (Vorlauf, Uhrzeit, an/aus) nur für Admins; abschalten für sich selbst im Profil.
  { label: 'Erinnerung', path: TASK_REMINDER_PATH, visible: adminOnly, keywords: ['Erinnerung', 'Glocke', 'Vorlauf', 'Fälligkeit', 'Arbeitstag vorher', 'Benachrichtigung'] },
  // Story 23.11: Wiederholungen (pausieren, fortsetzen, beenden; Vorlauf) nur für Admins; einstellen im Blatt der Aufgabe.
  { label: 'Wiederholungen', path: TASK_RECURRENCE_PATH, visible: adminOnly, keywords: ['Wiederholung', 'wiederkehrend', 'Serie', 'jeden Montag', 'Vorlauf', 'regelmäßig'] },
  // Alle sehen die Muster; „Muster erzeugen“ nur Admins.
  { label: 'Vorlagen', path: DOCUMENT_TEMPLATES_PATH, keywords: ['Belegvorlagen', 'Muster', 'Briefkopf', 'Layout'] },
  // Alle sehen Kreise und Prüfbericht; „Zählerstand setzen“ nur Admins.
  { label: 'Nummernkreise', path: NUMBER_RANGES_PATH, keywords: ['Zähler', 'Belegnummer', 'Rechnungsnummer', 'Angebotsnummer'] },
  // Alle sehen die Versandkosten lesend; ändern nur Admins (E7).
  { label: 'Versandkosten', path: SHIPPING_COSTS_PATH, keywords: ['Porto', 'Versand'] },
  // Story 25.1: Büro-Rollen sehen den Dubletten-Bericht der EANs.
  { label: 'EAN-Nummern', path: GTIN_SETTINGS_PATH, visible: (me) => me.role === 'user' || me.role === 'admin', keywords: ['EAN', 'GTIN', 'Barcode', 'Strichcode', 'doppelte EAN', 'Dubletten'] },
  // Alle sehen den Feldkatalog lesend; „Jetzt aus Shopware lesen“ nur Admins (Story 5.18).
  { label: 'Shopware-Felder', path: SHOPWARE_FIELDS_PATH, keywords: ['Feldzuordnung', 'Mapping', 'Zusatzfelder'] },
  // Feld-Abdeckung (Story 5.29): alle sehen den Bericht lesend.
  { label: 'Feld-Abdeckung', path: SHOPWARE_FIELD_COVERAGE_PATH, keywords: ['Füllgrad', 'offene Felder', 'Abdeckungsbericht'] },
  // E-Mail (Story 2.2): Seite der Gruppe `mail`.
  { label: 'Postausgang', path: MAIL_PATH, keywords: ['E-Mail', 'Versandliste', 'Absender', 'Tagesgrenze'] },
  // Verzeichnis der Personendaten (Story 18.2): nur lesen.
  { label: 'Personendaten', path: PRIVACY_PATH, keywords: ['Datenschutz', 'DSGVO', 'Auskunft', 'Anonymisieren', 'pii'] },
  // Anonymisierungen (Story 18.4): nur Admins; Frist zum Zurücknehmen als Karte darunter.
  { label: 'Anonymisierungen', path: ANONYMIZATIONS_PATH, visible: adminOnly, keywords: ['Datenschutz', 'DSGVO', 'Löschen', 'Anonymisieren', 'Zurücknehmen', 'Frist'] },
  // Zeiterfassung (Story 22.1): Mitarbeiter legt nur der Admin an.
  { label: 'Mitarbeiter', path: STAFF_EMPLOYEES_PATH, visible: adminOnly, keywords: ['Zeiterfassung', 'Personalnummer', 'Eintritt', 'Austritt', 'Stempeln'] },
  // Story 27.5: Etiketten gestalten Benutzer, Packplatz und Admin (eine Rechte-Tabelle `labels/access.ts`).
  { label: 'Etiketten', path: LABELS_PATH, visible: (me) => hasLabelRight(me.role, 'design'), keywords: ['Etikett', 'Etiketten', 'Label', 'Vorlage', 'Strichcode', 'Barcode', 'Drucken', 'Zebra', 'Artikeletikett', 'Lagerplatz'] },
  // Story 27.6: Drucker (Profile, Testdruck nur Admin) und Druckverlauf für alle, die drucken (ohne Packplatz: keine Einstellungen).
  { label: 'Drucker', path: LABEL_PRINTERS_PATH, visible: (me) => hasLabelRight(me.role, 'print') && me.role !== 'packplatz', keywords: ['Etikettendrucker', 'Druckerprofil', 'QZ Tray', 'ZPL', 'dpi', 'Testdruck', 'Kalibrierung', 'Zebra'] },
  { label: 'Druckverlauf', path: LABEL_PRINTS_PATH, visible: (me) => hasLabelRight(me.role, 'print') && me.role !== 'packplatz', keywords: ['Druckprotokoll', 'gedruckt', 'Etiketten gedruckt', 'Druckergebnis unklar'] },
  // Auf live liefert /me keine Testuhr (null): dort fehlt der Reiter (D2).
  { label: 'Testuhr', path: TEST_CLOCK_PATH, visible: (me) => Boolean(me.testClock), keywords: ['Datum', 'Uhrzeit'] },
];

/** Seiten, die die angemeldete Person in den Einstellungen sieht. */
export function visibleSettingsPages(me: Me): SettingsPageItem[] {
  return settingsPages.filter((p) => !p.visible || p.visible(me));
}

// ---------------------------------------------------------------------------
// Hauptpunkte und Reiter (Change „Einstellungen ordnen“, Rudolf 07.10.2026)

/**
 * Ein Reiter: eine eigene Seite (`page`) und/oder Gruppen der Schnittstelle (`groups`). Ohne Seite zeigt der Reiter die
 * Karten seiner Gruppen; mit Seite zeigt die Seite ihre Inhalte selbst, `cards: true` hängt die Karten der Gruppen
 * darunter (Technik › Betrieb). `keywords`: weitere Suchwörter, z. B. frühere Namen.
 */
export interface SettingsTabDef {
  id: string;
  label: string;
  page?: string;
  groups?: readonly string[];
  cards?: boolean;
  /** Verborgene Verwaltungsseite: auch ihre Einstellungs-Karten bleiben verborgen. */
  requirePage?: boolean;
  keywords?: readonly string[];
}

export interface SettingsSectionDef {
  id: string;
  label: string;
  icon: LucideIcon;
  tabs: readonly SettingsTabDef[];
}

/**
 * **Einzige Quelle** der Gliederung. Reihenfolge fest; neue Einstellungen zuerst als Feld/Karte in einen bestehenden
 * Reiter, sonst neuer Reiter, neuer Hauptpunkt nur bei absehbar ≥ 2 Reitern (Change §4.4). Gruppen, die hier fehlen,
 * stehen unter „Weitere“.
 */
export const SETTINGS_SECTIONS: readonly SettingsSectionDef[] = [
  {
    id: 'firma',
    label: 'Firma',
    icon: Building2,
    tabs: [
      { id: 'firmendaten', label: 'Firmendaten', groups: [COMPANY_GROUP], keywords: ['Firma', 'Briefkopf'] },
      { id: 'benutzer', label: 'Benutzer', page: USERS_PATH },
      { id: 'anmeldung', label: 'Anmeldung', groups: ['sign_in'] },
    ],
  },
  {
    id: 'belege',
    label: 'Belege',
    icon: ReceiptText,
    tabs: [
      { id: 'allgemein', label: 'Allgemein', groups: ['documents'] },
      { id: 'vorlagen', label: 'Vorlagen', page: DOCUMENT_TEMPLATES_PATH },
      { id: 'nummernkreise', label: 'Nummernkreise', page: NUMBER_RANGES_PATH },
    ],
  },
  {
    id: 'verkauf',
    label: 'Verkauf',
    icon: ShoppingCart,
    tabs: [
      { id: 'auftraege', label: 'Aufträge', groups: ['orders', 'order_checks', 'order_check_values'], keywords: ['Auftragsampel', 'Prüfungen', 'MRN', 'Ausfuhr', 'Versandart', 'DHL-Produkt', 'Zahlart'] },
      // Story 9.7: Gültigkeit der Angebote (Gruppe von quotes); die Angebotsnummer steht seit Story 9.8 im Nummernkreis „Angebot“.
      { id: 'angebote', label: 'Angebote', groups: ['quotes'], keywords: ['Angebot', 'Gültigkeit', 'gültig bis'] },
      { id: 'kunden', label: 'Kunden', groups: ['customers'] },
      // Story 14.1: Retourengründe und Nummernformat der Retouren (Gruppe von returns).
      { id: 'retouren', label: 'Retouren', groups: ['returns'], keywords: ['Retourengrund', 'Retourengründe', 'Retoure', 'Retourennummer'] },
      // Story 6.19: Grenzen der Verkaufskennzahlen (Rhythmus, Abwanderungsrisiko, ABC/XYZ) – Gruppe von orders.
      // Ticket 1.128: Ziele der Kennzahl-Kacheln (Ziel-Balken auf Startseite und Berichten) – Gruppe von reporting.
      { id: 'kennzahlen', label: 'Kennzahlen', groups: ['kpi_targets', 'sales_metrics'], keywords: ['Verkaufskennzahlen', 'ABC', 'XYZ', 'Abwanderungsrisiko', 'Bestellrhythmus', 'Ziele', 'Monatsziel'] },
    ],
  },
  { id: 'finanzen', label: 'Finanzen', icon: Landmark, tabs: [
      { id: 'steuer', label: 'Steuer', page: TAX_PATH },
      // Story 6.7: Checkout-Zahlarten (Gruppe billing); Story 13.2: Skonto-Kulanz, Kleinbetragsgrenze, Transit-Muster;
      // Story 13.22: Angaben für den VR-Bank-Abruf über EBICS (Gruppe ebics, früher finAPI 13.1); Story 13.3: PayPal-Abruf (Gruppe paypal).
      {
        id: 'zahlungen',
        label: 'Zahlungen',
        // Story 18.27: Annahmen der Vorschau Berichte › Zahlungseingänge (Gruppe incoming_forecast von reporting).
        groups: ['billing', 'ebics', 'paypal', 'incoming_forecast'],
        keywords: ['Zahlungseingänge', 'Vorschau', 'PayPal', 'Checkout', 'Skonto', 'Kulanz', 'Transit', 'Kleinbetrag', 'EBICS', 'camt', 'VR-Bank', 'Kontoumsätze', 'Client-ID', 'Transaction Search'],
      },
      // Story 16.1: DATEV-Kopfdaten und Konten (Gruppe datev von billing); seit Story 16.4 für Admins pflegbar, Prüfung im Command.
      { id: 'datev', label: 'DATEV', groups: ['datev'], keywords: ['Beraternummer', 'Mandant', 'Erlöskonto', 'Debitor', 'Buchungsstapel'] },
    ],
  },
  {
    id: 'lager-versand',
    label: 'Lager & Versand',
    icon: Truck,
    tabs: [
      { id: 'lager', label: 'Lager', groups: ['inventory', 'catalog', 'stock_planning'], keywords: ['Artikel', 'Varianten', 'Merkmale', 'Lagerkennzahlen', 'Ladenhüter', 'Überbestand'] },
      // Story 25.1: EAN-Register – Dubletten-Bericht (25.8: Einstellungen des Nummern-Pools).
      { id: 'ean-nummern', label: 'EAN-Nummern', page: GTIN_SETTINGS_PATH, keywords: ['EAN', 'GTIN', 'Barcode', 'Strichcode', 'doppelte EAN', 'Dubletten', 'Prüfziffer'] },
      // Story 10.1: Vorbelegung neuer Lieferanten am Artikel; Story 10.15: Mindestmarge.
      { id: 'einkauf', label: 'Einkauf', groups: ['purchasing'], keywords: ['Lieferanten', 'Lieferzeit', 'Mindestbestellmenge', 'Marge', 'Mindestmarge'] },
      { id: 'versandkosten', label: 'Versandkosten', page: SHIPPING_COSTS_PATH, groups: [SHIPPING_FREE_GROUP], keywords: ['versandkostenfrei', 'Versandkostenfrei', 'shippingFree'] },
      // Story 11.1: Etikettformat (Gruppe shipping) und DHL-Geschäftskundenkonto (Gruppe dhl, ohne geheime Zugänge).
      { id: 'versand', label: 'Versand', groups: ['shipping', 'dhl', 'dhl_express'], keywords: ['Etikett', 'Label', 'DHL', 'Abrechnungsnummer', 'Geschäftskundenportal', 'Druckformat', 'Auto-Druck', 'Lieferschein', 'Express', 'MyDHL', 'Kundennummer', 'Abholung'] },
    ],
  },
  {
    // Story 27.5: Etiketten-Galerie; Story 27.6: Seite Drucker (Profile, Testdruck, Gruppe `labels`) und Druckverlauf.
    id: 'drucken',
    label: 'Drucken',
    icon: Printer,
    tabs: [
      { id: 'etiketten', label: 'Etiketten', page: LABELS_PATH },
      { id: 'drucker', label: 'Drucker', page: LABEL_PRINTERS_PATH, groups: [LABELS_GROUP], keywords: ['Etikettendrucker', 'Druckerprofil', 'QZ Tray', 'ZPL', 'dpi', 'Standard-Vorlage', 'Testdruck', 'Zebra', 'Strichcode', 'Lesbarkeit', 'Druckpunkte', 'Strichhöhe', 'Etikettgröße'] },
      { id: 'verlauf', label: 'Druckverlauf', page: LABEL_PRINTS_PATH },
    ],
  },
  {
    // Story 22.1 (FR-73, Nachtrag §6): eine Seite „Zeiterfassung“ mit Reitern; spätere Stories ergänzen Arbeitszeit &
    // Rundung und Urlaub & Abwesenheit.
    id: 'zeiterfassung',
    label: 'Zeiterfassung',
    icon: Clock,
    tabs: [
      { id: 'mitarbeiter', label: 'Mitarbeiter', page: STAFF_EMPLOYEES_PATH },
      // Story 22.4: Modell-Vorlagen und Grenzen nach Arbeitszeitgesetz; Story 22.17: Rundung je Mitarbeiter (eigene Karte).
      {
        id: 'arbeitszeit-rundung',
        label: 'Arbeitszeit & Rundung',
        groups: [STAFF_WORK_TIME_GROUP, STAFF_TIME_ROUNDING_GROUP],
        keywords: ['Arbeitszeitmodell', 'Vorlage', 'Arbeitszeitgesetz', 'ArbZG', 'Pause', 'Ruhezeit', 'Höchstarbeitszeit', 'Rundung', 'Raster', 'angerechnet', 'Soll-Beginn'],
      },
      // Story 22.6: Abwesenheitsarten und Urlaubsregeln.
      { id: 'urlaub-abwesenheit', label: 'Urlaub & Abwesenheit', groups: [STAFF_LEAVE_GROUP], keywords: ['Urlaub', 'Abwesenheit', 'Abwesenheitsarten', 'Urlaubsanspruch', 'Resturlaub', 'Halbtag', 'Verfall', 'Übertrag'] },
      { id: 'funktionen-fristen', label: 'Funktionen & Fristen', groups: [STAFF_FUNCTIONS_GROUP], keywords: ['Pause-Knopf', 'Pause', 'Rückgängig', 'Stempeln', 'Zeiterfassung'] },
    ],
  },
  {
    id: 'e-mail',
    label: 'E-Mail & Aufgaben',
    icon: Mail,
    tabs: [
      { id: 'postausgang', label: 'Postausgang', page: MAIL_PATH, groups: [MAIL_GROUP] },
      { id: 'vorlagen', label: 'Vorlagen', page: MAIL_TEMPLATES_PATH },
      { id: 'aufgaben', label: 'Aufgaben und Chatter', groups: ['collab'] },
      // Story 24.1: Team-Chat (Höchstlänge, Ladeschritt); Story 24.13 ergänzt die Gruppe.
      { id: 'team-chat', label: 'Team-Chat', groups: ['chat'], keywords: ['Chat', 'Kanal', 'Nachricht', 'Höchstlänge'] },
    ],
  },
  {
    // Story 23.4: eigener Hauptpunkt für die Aufgaben-Verwaltung; Story 23.12: Vorlagen, Story 23.13: Erinnerung (nur Admin sichtbar).
    id: 'aufgaben',
    label: 'Aufgaben',
    icon: Tags,
    tabs: [
      { id: 'labels', label: 'Labels', page: TASK_LABELS_PATH },
      { id: 'vorlagen', label: 'Vorlagen', page: TASK_TEMPLATES_PATH },
      // Story 23.13: Gruppe nur für Suche und Verlauf; Karten zeichnet die eigene Seite (Zeitleiste), nur Admins.
      { id: 'erinnerung', label: 'Erinnerung', page: TASK_REMINDER_PATH, groups: ['task_reminder'], requirePage: true },
      // Story 23.11: Gruppe nur für Suche und Verlauf; Vorlauf und Liste zeichnet die eigene Seite, nur Admins.
      { id: 'wiederholungen', label: 'Wiederholungen', page: TASK_RECURRENCE_PATH, groups: ['task_recurrence'], requirePage: true },
    ],
  },
  {
    id: 'shop',
    label: 'Shop & Marktplätze',
    icon: Store,
    tabs: [
      { id: 'shopware', label: 'Shopware', groups: ['shopware', 'status_sync'], keywords: ['Statusabgleich'] },
      { id: 'shopware-felder', label: 'Shopware-Felder', page: SHOPWARE_FIELDS_PATH },
      { id: 'feld-abdeckung', label: 'Feld-Abdeckung', page: SHOPWARE_FIELD_COVERAGE_PATH },
      { id: 'ebay', label: 'eBay', groups: ['ebay'], keywords: ['Puffer', 'Bestand', 'Marktplatz', 'App-ID', 'Angebot', 'Richtlinie', 'Postleitzahl'] },
    ],
  },
  {
    id: 'schnittstellen',
    label: 'Schnittstellen',
    icon: Plug,
    tabs: [
      // Redesign Runde 2 ([RUDOLF] 01.11.2026, Variante A): Übersicht, Zugänge und Betriebsschalter sind eine Seite „Verbindungen“;
      // die alten Adressen zeigen dieselbe Seite.
      {
        id: 'uebersicht',
        label: 'Verbindungen',
        page: COUNTERPARTS_PATH,
        groups: ['counterpart_heartbeat', 'credential_watch'],
        cards: true,
        keywords: ['Meldetakt', 'Sollstellung', 'Heartbeat', 'Zugänge', 'Zugänge-Wächter', 'Ablaufdatum', 'Nachtprüfung', 'Betriebsschalter', 'off', 'shadow', 'live'],
      },
      {
        id: 'wechselkurse',
        label: 'Wechselkurse',
        page: EXCHANGE_RATES_PATH,
        groups: ['exchange_rates'],
        cards: true,
        keywords: ['EZB', 'Kurs', 'Währung', 'USD', 'Fremdwährung', 'Abrufzeit'],
      },
      { id: 'wiederholungen', label: 'Wiederholungen', groups: ['side_effects'], keywords: ['Außenwirkungen'] },
      { id: 'eigene-api', label: 'Eigene API', groups: ['api'], keywords: ['Schnittstelle', 'REST'] },
    ],
  },
  {
    id: 'ki',
    label: 'KI',
    icon: Sparkles,
    tabs: [
      { id: 'uebersicht', label: 'Übersicht', page: AI_PATH },
      { id: 'agenten', label: 'Agenten', page: AI_AGENTS_PATH },
      { id: 'budget', label: 'Budget', page: AI_BUDGET_PATH },
      // Story 5.102: Gruppe nur für die Suche; Karten zeichnet die eigene Seite.
      { id: 'artikel-assistent', label: 'Artikel-Assistent', page: AI_ARTICLE_ASSISTANT_PATH, groups: ['ai_article_assistant'], requirePage: true },
      { id: 'mcp-schluessel', label: 'MCP-Schlüssel', page: MCP_KEYS_PATH, groups: ['mcp'], cards: true, requirePage: true },
      { id: 'modelle', label: 'Modelle', groups: ['ai'], keywords: ['KI-Modelle'] },
    ],
  },
  {
    id: 'datenschutz',
    label: 'Datenschutz',
    icon: ShieldCheck,
    tabs: [
      { id: 'personendaten', label: 'Personendaten', page: PRIVACY_PATH, keywords: ['Verzeichnis', 'DSGVO'] },
      { id: 'anonymisierungen', label: 'Anonymisierungen', page: ANONYMIZATIONS_PATH, groups: ['privacy'], cards: true },
      // Story 18.5: Löschfristen je Datenart (leer = nicht festgelegt, nichts gelöscht); ändern nur Admins.
      { id: 'loeschfristen', label: 'Löschfristen', groups: ['privacy_retention'], keywords: ['Löschfrist', 'Aufbewahrung', 'DSGVO', 'Löschlauf'] },
    ],
  },
  {
    id: 'technik',
    label: 'Technik',
    icon: Wrench,
    tabs: [
      { id: 'betrieb', label: 'Betrieb', page: OPERATIONS_PATH, groups: ['operations'], cards: true },
      { id: 'archiv-sicherung', label: 'Archiv und Sicherung', page: ARCHIVE_BACKUP_PATH, groups: [ARCHIVE_BACKUP_GROUP], cards: true },
      { id: 'system', label: 'System', groups: ['system'] },
      { id: 'listen', label: 'Listen', groups: ['lists', 'metrics'], keywords: ['Kennzahlen', 'Tageslauf'] },
      { id: 'zeitjobs', label: 'Zeitjobs', groups: ['timed_jobs'], keywords: ['Nachholen', 'Pause', 'Tageslauf'] },
      // Story 17.20: Grenzen der Regeln (Ebenen, Bedingungen, Aktionen, Priorität) – Gruppe von rules.
      { id: 'automatik', label: 'Automatik', groups: ['rule_limits', 'rule_waiting'], keywords: ['Regel', 'Regeln', 'Grenzen', 'Aktionen', 'Bedingungen', 'Priorität', 'Warten', 'Warte-Schritt'] },
      { id: 'testuhr', label: 'Testuhr', page: TEST_CLOCK_PATH },
    ],
  },
  // Eigener Hauptpunkt bis nach dem Go-live (Rudolf 07.10.2026, Change „Einstellungen ordnen“ F3).
  {
    id: 'start',
    label: 'Start & Umschalttag',
    icon: Rocket,
    tabs: [
      { id: 'probephase', label: 'Probephase', page: TRIAL_PATH },
      {
        id: 'vergleichsbericht',
        label: 'Vergleichsbericht',
        page: COMPARISON_REPORT_PATH,
        groups: ['comparison_report'],
        cards: true,
        keywords: ['Vergleich', 'Xentral', 'Differenz', 'erwartet', 'unerklärt'],
      },
      { id: 'umschalttag', label: 'Umschalttag', page: CUTOVER_PATH },
    ],
  },
];

/** Hauptpunkt für Gruppen ohne Zuordnung (z. B. „Beispiel (Test)“); je Gruppe ein Reiter. */
export const OTHER_SECTION = { id: 'weitere', label: 'Weitere', icon: Ellipsis } as const;

/** Adresse eines Reiters. */
export function settingsTabPath(section: string, tab: string): string {
  return `${SETTINGS_PATH}/${section}/${tab}`;
}

/** Ein sichtbarer Reiter mit den Gruppen, die die Person sieht. */
export interface SettingsNavTab {
  key: string;
  id: string;
  label: string;
  path: string;
  page: SettingsPageItem | null;
  groups: SettingGroupSummary[];
  /** Karten der Gruppen im Rahmen zeigen (Reiter ohne eigene Seite oder mit `cards`). */
  cards: boolean;
  keywords: readonly string[];
}

export interface SettingsNavSection {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Adresse des ersten sichtbaren Reiters (Ziel des Hauptpunkts). */
  path: string;
  tabs: SettingsNavTab[];
}

/**
 * Navigation nach Hauptpunkten: je Hauptpunkt die sichtbaren Reiter in fester Reihenfolge. Ein Reiter mit Seite ist
 * sichtbar, wenn die Seite es ist; ein Reiter nur aus Gruppen, wenn die Schnittstelle mindestens eine davon liefert.
 * Nicht zugeordnete Gruppen stehen unter „Weitere“; Hauptpunkte ohne sichtbaren Reiter fallen weg. Rein – gezeigt wird
 * nur, was die Person ohnehin sieht.
 */
export function settingsNavSections(groups: readonly SettingGroupSummary[], pages: readonly SettingsPageItem[]): SettingsNavSection[] {
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const pageByPath = new Map(pages.map((p) => [p.path, p]));
  const mapped = new Set(SETTINGS_SECTIONS.flatMap((s) => s.tabs.flatMap((t) => t.groups ?? [])));
  const sections: SettingsNavSection[] = SETTINGS_SECTIONS.map((section) => {
    const tabs = section.tabs.flatMap((tab): SettingsNavTab[] => {
      const tabGroups = (tab.groups ?? []).flatMap((id) => groupById.get(id) ?? []);
      const page = tab.page ? (pageByPath.get(tab.page) ?? null) : null;
      if (tab.requirePage && !page) return [];
      // Seite verborgen, Gruppen sichtbar: der Reiter zeigt nur die Karten der Gruppen (sie verschwinden nie ganz).
      if (!page && tabGroups.length === 0) return [];
      const keywords = [...(page?.keywords ?? []), ...(tab.keywords ?? [])];
      const path = page?.path ?? settingsTabPath(section.id, tab.id);
      return [{ key: `${section.id}/${tab.id}`, id: tab.id, label: tab.label, path, page, groups: tabGroups, cards: !page || tab.cards === true, keywords }];
    });
    return { id: section.id, label: section.label, icon: section.icon, path: tabs[0]?.path ?? '', tabs };
  });
  const rest = groups.filter((g) => !mapped.has(g.id));
  const otherTabs = rest.map((g): SettingsNavTab => {
    const path = settingsTabPath(OTHER_SECTION.id, g.id);
    return { key: `${OTHER_SECTION.id}/${g.id}`, id: g.id, label: g.label, path, page: null, groups: [g], cards: true, keywords: [] };
  });
  sections.push({ ...OTHER_SECTION, path: otherTabs[0]?.path ?? '', tabs: otherTabs });
  return sections.filter((s) => s.tabs.length > 0);
}

/** Hauptpunkt und Reiter zu einer Seite (`page`) oder Gruppe (`group`), z. B. für den Rahmen einer Seite. */
export function findSettingsTab(
  sections: readonly SettingsNavSection[],
  where: { page?: string | null; group?: string | null },
): { section: SettingsNavSection; tab: SettingsNavTab } | null {
  for (const section of sections) {
    for (const tab of section.tabs) {
      if (where.page ? tab.path === where.page : where.group ? tab.groups.some((g) => g.id === where.group) : false) return { section, tab };
    }
  }
  return null;
}

/** Alte Seiten-Adressen (vor dem 07.10.2026) → neue Adresse. */
const LEGACY_PAGES: Readonly<Record<string, string>> = {
  benutzer: USERS_PATH,
  schnittstellen: COUNTERPARTS_PATH,
  betriebsschalter: SWITCHES_PATH,
  zugaenge: CREDENTIALS_PATH,
  testuhr: TEST_CLOCK_PATH,
  ki: AI_PATH,
  steuer: TAX_PATH,
  'e-mail-vorlagen': MAIL_TEMPLATES_PATH,
  betrieb: OPERATIONS_PATH,
  versandkosten: SHIPPING_COSTS_PATH,
  'shopware-felder': SHOPWARE_FIELDS_PATH,
  belegvorlagen: DOCUMENT_TEMPLATES_PATH,
  nummernkreise: NUMBER_RANGES_PATH,
  [MAIL_GROUP]: MAIL_PATH,
};

/**
 * Ziel einer alten oder verkürzten Adresse (ohne Suchparameter) oder `null`, wenn sie keine ist:
 * `/einstellungen/<alte-seite>` und `/einstellungen/<gruppen-id>` → neuer Reiter (Gruppe ohne Zuordnung → „Weitere“),
 * `/einstellungen/<hauptpunkt>` → erster sichtbarer Reiter, `/einstellungen/benutzer/<id>` → Firma › Benutzer › <id>.
 * Hauptpunkte gehen vor alten Namen (`/einstellungen/ki` und `/einstellungen/schnittstellen` sind beides).
 */
export function legacySettingsTarget(pathname: string, sections: readonly SettingsNavSection[]): string | null {
  let parts: string[];
  try {
    parts = pathname.replace(/\/+$/, '').split('/').slice(2).map(decodeURIComponent);
  } catch {
    return null;
  }
  const [first, second, ...more] = parts;
  if (first === undefined || more.length > 0) return null;
  if (second !== undefined) return first === 'benutzer' ? `${USERS_PATH}/${encodeURIComponent(second)}` : null;
  const section = sections.find((s) => s.id === first);
  if (section) return section.path;
  const page = Object.hasOwn(LEGACY_PAGES, first) ? LEGACY_PAGES[first] : undefined;
  if (page) return page;
  // Gruppen-ID: Reiter der Gruppe (auch unter „Weitere“); unbekannte Namen sind keine alte Adresse.
  for (const s of sections) {
    const tab = s.tabs.find((t) => t.groups.some((g) => g.id === first));
    if (tab) return tab.path;
  }
  return null;
}

/** Ein Treffer der Einstellungs-Suche. */
export interface SettingsSearchHit {
  /** Eindeutig je Treffer (für React und `aria-activedescendant`). */
  id: string;
  kind: 'topic' | 'page' | 'card' | 'setting';
  /** Pfad „Hauptpunkt › Reiter › Karte › Feld“ (Karte nur bei Abschnitten), letzter Teil = der Treffer selbst. */
  trail: string[];
  /** Ziel: Seite, bei Karte bzw. Feld mit Suchparameter `karte` bzw. `feld` (Sprung und Hervorhebung). */
  href: string;
}

interface IndexEntry {
  hit: SettingsSearchHit;
  /** Eigene Texte (Name, Beschreibung, Stichworte), gefaltet. */
  own: string;
  /** Nur der Name, gefaltet (Feld-Label-Treffer vor Beschreibung). */
  name: string;
  /** Eigene Texte und Namen der Ebenen darüber, gefaltet („Firma IBAN“). */
  all: string;
}

export type SettingsSearchIndex = readonly IndexEntry[];

function entry(hit: SettingsSearchHit, own: readonly string[], context: readonly string[]): IndexEntry {
  const name = searchText(hit.trail.at(-1) ?? '');
  const ownText = searchText(own.join(' '));
  return { hit, own: ownText, name, all: `${ownText} ${searchText(context.join(' '))}` };
}

/**
 * Suchindex aus der Navigation (Rechte unverändert: nur, was die Navigation zeigt): Hauptpunkte, Reiter (mit
 * Stichworten, auch frühere Namen), Karten (Abschnitte einer Gruppe) und Einstellungen (Name, Beschreibung).
 * Reihenfolge wie in der Navigation. `kind` bleibt `topic` (Hauptpunkt) und `page` (Reiter).
 */
export function settingsSearchIndex(sections: readonly SettingsNavSection[]): SettingsSearchIndex {
  const index: IndexEntry[] = [];
  for (const sec of sections) {
    // Hauptpunkt nur, wenn kein gleichnamiger Reiter darunter steht (sonst doppelter Treffer).
    const shadowed = sec.tabs.some((t) => searchText(t.label) === searchText(sec.label));
    if (sec.tabs.length > 0 && !shadowed) index.push(entry({ id: `topic:${sec.id}`, kind: 'topic', trail: [sec.label], href: sec.path }, [sec.label], []));
    for (const tab of sec.tabs) {
      index.push(entry({ id: `tab:${tab.key}`, kind: 'page', trail: [sec.label, tab.label], href: tab.path }, [tab.label, ...tab.keywords], [sec.label]));
      for (const group of tab.groups) {
        const cards = new Set<string>();
        for (const s of group.settings) {
          const section = s.section;
          if (section !== null && !cards.has(section)) {
            cards.add(section);
            const cardId = `${group.id}:${section}`;
            index.push(
              entry(
                { id: `card:${cardId}`, kind: 'card', trail: [sec.label, tab.label, section], href: `${tab.path}?karte=${encodeURIComponent(cardId)}` },
                [section],
                [sec.label, tab.label],
              ),
            );
          }
          const trail = section === null ? [sec.label, tab.label, s.label] : [sec.label, tab.label, section, s.label];
          index.push(
            entry({ id: `setting:${s.key}`, kind: 'setting', trail, href: `${tab.path}?feld=${encodeURIComponent(s.key)}` }, [s.label, s.description], trail.slice(0, -1)),
          );
        }
      }
    }
  }
  return index;
}

/** Höchstzahl gezeigter Treffer. */
export const SETTINGS_SEARCH_LIMIT = 20;

/**
 * Suche in den Einstellungen: Faltung wie überall (`searchText`: Groß/klein egal, ä = ae, ß = ss); jedes Wort muss im
 * Treffer oder den Ebenen darüber vorkommen, mindestens eines im Treffer selbst. Reihenfolge: Hauptpunkte und Reiter, dann
 * Karten, dann Einstellungen mit Treffer im Namen, dann übrige Einstellungen (Beschreibung); sonst wie die Navigation.
 */
export function searchSettings(index: SettingsSearchIndex, query: string, limit = SETTINGS_SEARCH_LIMIT): SettingsSearchHit[] {
  const words = searchText(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const ranked: { hit: SettingsSearchHit; rank: number; order: number }[] = [];
  index.forEach((e, order) => {
    if (!words.every((w) => e.all.includes(w)) || !words.some((w) => e.own.includes(w))) return;
    const rank = e.hit.kind === 'topic' || e.hit.kind === 'page' ? 0 : e.hit.kind === 'card' ? 1 : words.every((w) => e.name.includes(w) || !e.own.includes(w)) ? 2 : 3;
    ranked.push({ hit: e.hit, rank, order });
  });
  return ranked
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, limit)
    .map((r) => r.hit);
}
